import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";

import { Content } from "./Content";
import { IApiServiceError, useDealActions, useDealIdSelector } from "store";
import { useActionHook } from "hooks";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { CryptoSearch } from "./CryptoSearch";
import { DealRoleGuard } from "components/guards";
import { RoleName } from "../../../constants";

interface Props {
  onClose: () => void;
  cryptoIcon: string | null;
}

export const Main: React.FC<Props> = ({ onClose, cryptoIcon }) => {
  const dealId = useDealIdSelector();

  const { searchCryptocurrencyAction, addDealCryptocurrencyAction, getDealCryptocurrencyAction } = useDealActions();
  // const { errorModal } = useModals();

  const [loading, setLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState(!!cryptoIcon);
  const [searchValue, setSearchValue] = useState<string>("");
  const [cryptoCurrency, setCryptocurrency] = useState<{ key: string; value: string } | null>(null);
  const [connectError, setConnectError] = useState<string>("");

  const { action: searchCryptoAction, data: cryptoOptions, resetData } = useActionHook(searchCryptocurrencyAction);
  const { action: getDealCryptoAction, data } = useActionHook(getDealCryptocurrencyAction);
  const { action: addDealCryptoAction } = useActionHook(addDealCryptocurrencyAction, {
    onSuccess: async () => {
      dealId && (await getDealCryptoAction(dealId));
      setIsConnected(true);
    }
  });

  const addCryptoToDeal = useCallback(async () => {
    try {
      if (!dealId) return;
      if (!cryptoCurrency) return;
      setLoading(true);
      if (connectError) setConnectError("");
      await addDealCryptoAction(cryptoCurrency.key, dealId);
      setIsConnected(true);
    } catch (error) {
      setConnectError((error as IApiServiceError).message);
    } finally {
      setLoading(false);
    }
  }, [cryptoCurrency, searchValue, dealId, connectError]);

  useEffect(() => {
    if (!cryptoIcon) return;
    if (!dealId) return;
    getDealCryptoAction(dealId);
  }, []);

  useEffect(() => {
    if (searchValue === "") {
      return;
    }

    searchCryptoAction(searchValue);
  }, [searchValue]);

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 100 }}>
      <Box flexGrow="1" sx={{ overflowY: "auto" }} padding="30px 30px 40px 30px">
        {isConnected ? (
          data && <Content changeCrypto={() => setIsConnected(false)} data={data} />
        ) : (
          <CryptoSearch
            inputValue={searchValue}
            setInputValue={setSearchValue}
            resetData={resetData}
            selectCrypto={setCryptocurrency}
            options={cryptoOptions || []}
          />
        )}
      </Box>
      <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap="21px"
          padding="29px"
          boxShadow="0 -1px 5px 1px rgba(0, 0, 0, 0.08)"
        >
          {!isConnected && (
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
          )}
          {!isConnected && (
            <Button disabled={loading || !searchValue} onClick={addCryptoToDeal} variant="contained">
              Connect
            </Button>
          )}
          {Boolean(cryptoIcon) && isConnected && (
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          )}
        </Stack>
      </DealRoleGuard>
    </AsyncContentMui>
  );
};
